import { cn } from "@/lib/utils";
import { Gem } from "lucide-react";
import { Link } from "react-router";

interface DealStatsBadgeProps {
  // null = no billing access (non-admin); rendered non-interactive.
  billingLink: string | null;
  tracked: number;
  cap: number;
  className?: string;
}

export const DealStatsBadge = ({
  billingLink,
  tracked,
  cap,
  className = "",
}: DealStatsBadgeProps) => {
  const ratio = cap > 0 ? tracked / cap : 0;
  const atCap = ratio >= 1;
  const nearCap = ratio >= 0.8 && !atCap;

  const baseClassName = cn(
    "inline-flex h-8 items-center gap-2 rounded-md border bg-background px-2.5",
    billingLink && "transition-colors hover:bg-foreground/[0.03]",
    atCap
      ? "border-destructive/40"
      : nearCap
        ? "border-amber-400/60"
        : "border-border",
    className
  );

  const content = (
    <>
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[10px] font-semibold uppercase leading-none tracking-widest",
          atCap
            ? "text-destructive"
            : nearCap
              ? "text-amber-600 dark:text-amber-400"
              : "text-muted-foreground/70"
        )}
      >
        <Gem className="h-3 w-3 shrink-0" />
        <span className="pt-px">Premium</span>
      </span>
      <span className="font-mono text-xs tabular-nums leading-none">
        <span
          className={cn(
            atCap
              ? "text-destructive"
              : nearCap
                ? "text-amber-700 dark:text-amber-300"
                : "text-foreground"
          )}
        >
          {tracked}
        </span>
        <span className="text-muted-foreground/50">{" / "}{cap}</span>
      </span>
    </>
  );

  if (!billingLink) {
    return (
      <span
        aria-label={`Premium deals: ${tracked} of ${cap}. View only — contact a workspace admin to change the plan.`}
        title="View only — contact a workspace admin to change the plan"
        className={baseClassName}
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      to={billingLink}
      aria-label={`Premium deals: ${tracked} of ${cap}. Manage subscription.`}
      title="Manage subscription"
      className={baseClassName}
    >
      {content}
    </Link>
  );
};
